import {FormsModule} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {AppConfigService} from "./config/app-config.service";
import {AuthService} from "./api/auth/auth.service";
import {ErrorComponent} from "./error/error.component";
import {DirtyDataWriterService} from "./api/dirtyData/dirty-data-writer.service";
import {OAuthStorage} from "angular-oauth2-oidc";
import {Browser} from "@capacitor/browser";
import {Preferences} from "@capacitor/preferences";
import {SpinnerComponent} from "./spinner/spinner.component";

import {LoggerService} from "./api/logging/logger.service";
import {addIcons} from 'ionicons';
import packageJson from "../../package.json"

import {
  IonApp,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonMenu,
  IonRouterOutlet,
  IonText,
  IonTitle,
  IonToolbar
} from "@ionic/angular/standalone";
import {
  card,
  chatboxEllipses,
  documentText,
  informationCircle,
  logoFacebook,
  logOutOutline,
  mail,
  person,
  pricetag,
  settingsOutline
} from "ionicons/icons";
import {Router} from "@angular/router";
import {SplashScreenService} from "./api/splash-screen/splash-screen.service";
import {SubscriptionService} from "./api/subscription/subscription.service";
import {SafeAreaService} from "./api/safe-area/safe-area.service";
import {UserContextService} from "./api/userContext/user-context.service";
import {PostHogService} from "./api/posthog/posthog.service";
import {openSentryFeedback} from "./shared/feedback/sentry-feedback";

export function setServicesFactory(appConfigService: AppConfigService,
                                   authService: AuthService,
                                   splashScreenService : SplashScreenService,
                                   subscriptionService : SubscriptionService,
                                   loggerService: LoggerService,
                                   postHogService: PostHogService) {
  loggerService.debug('Setting up services...');
  return splashScreenService
    .initialise(authService, subscriptionService)
    .then(() => appConfigService.loadConfig())
    .then(() => postHogService.initialise(
      appConfigService.postHogApiKey ?? '',
      appConfigService.postHogHost ?? ''
    ))
    .then(() => authService.init())
    .then(
      () => loggerService.debug('Services set up')
    );
}

export function storageFactory(): OAuthStorage {
  return localStorage
}

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule,
    CommonModule,
    ErrorComponent,
    SpinnerComponent,
    IonApp,
    IonHeader,
    IonToolbar,
    IonMenu,
    IonTitle,
    IonContent,
    IonItem,
    IonIcon,
    IonText,
    IonRouterOutlet
  ]
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly dirtyDataWriterService = inject(DirtyDataWriterService);
  protected readonly authService = inject(AuthService);
  protected readonly userContextService = inject(UserContextService);
  private readonly router = inject(Router);
  private readonly logger = inject(LoggerService);
  private readonly safeAreaService = inject(SafeAreaService);
  private readonly postHogService = inject(PostHogService);
  private readonly appConfigService = inject(AppConfigService);
  protected readonly subscriptionService =
    inject(SubscriptionService);

  @ViewChild('mainMenu', {static: true}) mainMenu?: IonMenu;

  readonly appVersion = packageJson.version;

  constructor() {
    addIcons({
      logOutOutline, person, logoFacebook, documentText, mail,
      informationCircle, card, chatboxEllipses, pricetag, settingsOutline
    });
  }

  private static readonly APP_INSTALLED_KEY =
    'hec_app_installed';

  ngOnInit() {
    this.dirtyDataWriterService.startPeriodicTask();
    this.safeAreaService.initialise().then(
      () => this.logger.info('Safe area initialised')
    );
    this.captureStartupEvents();
  }

  private async captureStartupEvents(): Promise<void> {
    await this.postHogService.whenReady();
    await this.captureFirstOpenIfNew();
    this.postHogService.capture('app_opened');
  }

  private async captureFirstOpenIfNew(): Promise<void> {
    if (!this.appConfigService.isMobile) return;
    const { value } = await Preferences.get({
      key: AppComponent.APP_INSTALLED_KEY
    });
    if (!value) {
      this.postHogService.capture('app_installed');
      await Preferences.set({
        key: AppComponent.APP_INSTALLED_KEY,
        value: 'true'
      });
    }
  }

  ngOnDestroy() {
    this.dirtyDataWriterService.stopPeriodicTask();
  }

  async accountSettings() {
    await this.authService.openAccountSettings();
    await this.closeMenu();
  }

  async closeMenu() {
    await this.mainMenu?.close();
  }

  async openExternalLink(url: string) {
    await Browser.open({url: url});
    await this.closeMenu();
  }

  async navigateTo(path: string) {
    this.logger.debug(`Navigating to ${path}`);
    await this.router.navigate([path])
    // close the menu after navigation
    await this.closeMenu();
  }

  async manageSubscription() {
    await this.subscriptionService
      .openManagementPage();
    await this.closeMenu();
  }

  async reportProblem(): Promise<void> {
    await openSentryFeedback();
    await this.closeMenu();
  }

  async logout() {
    this.authService.logout();
    await this.closeMenu();
  }
}
